<template>
  <div class="admin-login-wrapper">
    <div class="login-card">
      <h3 class="login-title">Admin Sign In</h3>

      <form @submit.prevent="onSubmit" class="login-form" novalidate>
        <div class="form-group">          
          <el-input id="username" v-model="username" type="text" placeholder="Username" required autocomplete="username" />
        </div>

        <div class="form-group">          
          <el-input
            id="password"            
            v-model="password"
            type="password"
            placeholder="Password"
            required
            show-password
            autocomplete="current-password"
          />          
        </div>

        <div class="form-actions">
          <button class="btn btn-primary" :disabled="loading">
            {{ loading ? 'Signing in...' : 'Sign in' }}
          </button>
        </div>

        <div v-if="error && Object.keys(error).length > 0" class="error-msg" role="alert">{{ error }}</div>
      </form>
    </div>
  </div>
</template>


<script setup>
import { ref, getCurrentInstance } from 'vue';
import apiClient from '@/apiClient';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { TIPSTAG_ERROR } from '@/constants';

const { proxy } = getCurrentInstance();
const router = useRouter();
 const store = useStore();

const username = ref('');
const password = ref('');
const loading = ref(false);
const error = ref({});


async function onSubmit() {
  error.value = '';
  if (!username.value || !password.value) {
    error.value = 'Please enter username and password.';
    return;
  }

  loading.value = true;
  try {
    const response = await apiClient.post('/Admin/loginAdmin', {
      accountName: username.value,
      password: password.value,
    });
    if (response && response.data) {
      
      proxy.$handleTips(response.data.tag, response.data.msg, error);

      if(response.data.tag === 'success') {      
        
        error.value = {};
        
        store.dispatch('login', { user: response.data.user, token: response.data.token });      

        setTimeout(() => {
          router.push({ name: 'AdminDashboard' }).catch(() => {});
        }, 1000);
      }      
    }
  } catch (e) {    
    error.value = (e?.response?.data?.msg) || 'Network error';
    if(e.response && e.response.data && e.response.status === 400) {    
      // proxy.$handleTips(e.response.data.tag, e.response.data.msg, e);
    } else {
      proxy.$handleTips(TIPSTAG_ERROR, 'Network error occurred. Please try again later.', e);
    }
  } finally {
    loading.value = false;
  }
}


</script>


<style scoped>

.admin-login-wrapper {
  min-height: calc(75vh - 84px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 48px 16px;
  background: var(--page-bg, #0f2330);
}


.login-card {
  width: 420px;
  max-width: calc(100% - 32px);
  background: transparent;   /* no card background */
  border-radius: 8px;
  padding: 18px 0;           
  box-shadow: none;          /* remove shadow */
  color: #fff;
}


.login-title {
  margin: 0 0 12px 0;
  color: var(--brand, #13c2c2);
  font-weight: 700;
  font-size: 1.1rem;
  text-align: center;
  padding-bottom: 20px;
}

/* inputs: bright white background, dark text */
.login-form .form-group {
  margin-bottom: 12px;
  padding: 0 6px;
}

/* Element Plus input custom styles for white background */
:deep(.el-input__wrapper) {
  background: #ffffff !important;
  border: 1px solid rgba(0,0,0,0.12) !important;
  box-shadow: none !important;
  padding: 10px 12px !important;
}

:deep(.el-input__wrapper:hover),
:deep(.el-input__wrapper.is-focus) {
  border-color: var(--brand, #13c2c2) !important;
  box-shadow: 0 0 0 1px rgba(19,194,194,0.2) !important;
}

:deep(.el-input__inner) {
  color: #0b1012 !important;
  font-size: 0.95rem !important;
}

:deep(.el-input__inner::placeholder) {
  color: rgba(0,0,0,0.45) !important;
}

/* Password show/hide icon */
:deep(.el-input__suffix-inner .el-icon) {
  color: rgba(0,0,0,0.65) !important;
}

:deep(.el-input__suffix-inner .el-icon:hover) {
  color: rgba(0,0,0,0.85) !important;
}


.form-actions {
  display: flex;
  justify-content: center;
  margin-top: 8px;
  padding: 0 6px;        /* align horizontal padding with input wrapper */
  box-sizing: border-box;
}

/* make the sign-in button exactly the same width as the inputs */
.form-actions .btn {
  width: 100%;
  box-sizing: border-box;
}

.btn {
  cursor: pointer;
  border: none;
  padding: 10px 18px;
  border-radius: 6px;
  font-size: 0.95rem;
}

.btn-primary {
  background: var(--brand, #13c2c2);
  color: #0b1012;
  font-weight: 600;
}

.btn[disabled] {
  opacity: 0.6;
  cursor: not-allowed;
}

.error-msg {
  margin-top: 12px;
  color: #ff6b6b;
  font-size: 0.9rem;
  text-align: center;
}

/* small screens */
@media (max-width: 480px) {
  .login-card { padding: 12px 0; }
  .login-title { font-size: 1rem; padding-left: 4px; }
  .form-actions .btn { width: 80%; min-width: 120px; }
}
</style>
